import type { ThemeStylesDto } from './schemas/theme-styles.schema';

// The fonts a theme names and where Google serves them, shared so the server writes the same stylesheet links into a
// page's head that the storefront would otherwise add after its script ran — under the same ids, so neither loads twice.

/** The family the storefront's own chrome is set in, which its page loads for every store. */
export const SHELL_FONT = 'Plus Jakarta Sans';

/** Names that are the browser's own families, never Google's. */
const GENERIC_RE = /^(system-ui|sans-serif|serif|monospace|cursive|fantasy|inherit|initial|ui-[a-z-]+|-apple-system|arial|helvetica)$/i;

/** The weights a theme's text and headings use. */
const WEIGHTS = 'wght@400;500;600;700';

// Reads the first family of a font stack, without quotes.
const firstFamily = (stack: string): string => stack.split(',')[0].replace(/['"]/g, '').trim();

// Lists the families a theme names that Google serves, each once, leaving out the one the page loads for its chrome.
export function themeFontFamilies(styles: Pick<ThemeStylesDto, 'fonts'>): string[] {
  const named = [styles.fonts.heading, styles.fonts.body, styles.fonts.accent].map(firstFamily);
  return [...new Set(named)].filter((family) => family !== '' && !GENERIC_RE.test(family) && family.toLowerCase() !== SHELL_FONT.toLowerCase());
}

// Builds a family's Google Fonts address, with the theme's weights or with its regular weight alone.
export const googleFontHref = (family: string, weighted: boolean): string =>
  `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}${weighted ? `:${WEIGHTS}` : ''}&display=swap`;

// Names the stylesheet link one family is loaded by, wherever it is written.
export const fontLinkId = (family: string): string => `xe-font-${family.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
